import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';

import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisLanguageService,
	AlarisToasterService,
	ErrorNotifierConfig,
	ExtendableRefBookService,
	sortData,
} from '@campaign-portal/components-library';

import {
	CreateResponse,
	DeleteRequest,
	DeleteResponse,
	ReadResponse,
} from '@campaign-portal/namespace/common/implementations';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { SenderSubscription } from '@campaign-portal/namespace/entities/sender-id/specs';
import { SenderSubscriptionEntity } from '../types';

type SenderSubscriptionApiResponse = SenderSubscription<exist>;

interface UpdateOptions {
	silentRefresh?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class SendersListService
	extends ExtendableRefBookService<SenderSubscriptionEntity<exist>>
	implements AbstractCRUDService
{
	readonly create = this.update;

	readonly active$ = new BehaviorSubject<SenderSubscriptionEntity<exist>[]>([]);

	constructor(
		private readonly api: AlarisApiService,
		private readonly alarisToaster: AlarisToasterService,
		private readonly langService: AlarisLanguageService
	) {
		super();
		this.list$.subscribe((list) => {
			this.active$.next(list.filter((sender) => sender.isActive()));
		});
	}

	get entity(): string {
		return this.langService.translate('notifications.entities.sender');
	}

	get title(): string {
		return this.langService.translate('notifications.titles.sender');
	}

	override load(): Observable<ReadResponse<SenderSubscriptionEntity<exist>[]>> {
		return this.api
			.loader<ReadResponse<SenderSubscriptionApiResponse[]>>('Senders.Read', {}, this.loading$, this.errorNotifier)
			.pipe(
				map((resp) => {
					const entities = resp.Data.map((s) => this.toEntity(s));
					sortData(entities, 'name');
					return super.process({ ...resp, Data: entities });
				})
			);
	}

	read(params?: RPCRequestParams): Observable<ReadResponse<SenderSubscriptionEntity<exist>[]>> {
		return this.api
			.loader<ReadResponse<SenderSubscriptionApiResponse[]>>('Senders.Read', params, this.loading$, this.errorNotifier)
			.pipe(map((resp) => ({ ...resp, Data: resp.Data.map((s) => this.toEntity(s)) })));
	}

	toEntity(sender: SenderSubscriptionApiResponse): SenderSubscriptionEntity<exist> {
		return new SenderSubscriptionEntity(sender);
	}

	requestSenderId(
		senderIds: SenderSubscription | SenderSubscription[]
	): Observable<CreateResponse<SenderSubscription<exist>>> {
		const Entities = Array.isArray(senderIds) ? senderIds : [senderIds];
		const params: RPCRequestParams = { Data: { Entities } };
		const notify = (response: CreateResponse<SenderSubscription<exist>>): void => {
			const message = this.langService.translate('notifications.actions.request', { entity: this.entity });
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<
				CreateResponse<SenderSubscription<exist>>
			>('Senders.Create', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap((resp) => {
					if (resp.Success) {
						this.refresh$.next();
					}
				})
			);
	}

	update(
		sender: SenderSubscription,
		{ silentRefresh = false }: UpdateOptions = {}
	): Observable<CreateResponse<SenderSubscription>> {
		const notify = (response: CreateResponse<SenderSubscription>): void => {
			if (response.Success) {
				const message = this.langService.translate(
					sender.id ? 'notifications.actions.update' : 'notifications.actions.create',
					{
						entity: this.entity,
						name: sender.name,
					}
				);
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<
				CreateResponse<SenderSubscription>
			>(`Senders.${sender.id ? 'Update' : 'Create'}`, { Data: { Entities: [sender] } }, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap((resp) => {
					if (resp.Success) {
						this.refresh$.next({ silent: silentRefresh });
					}
				})
			);
	}

	delete(id: Id<exist>): Observable<DeleteResponse<SenderSubscription>> {
		const params: DeleteRequest<SenderSubscription<exist>> = { Data: { Ids: [id] } };
		const notify = (response: DeleteResponse<SenderSubscription>): void => {
			if (response.Success) {
				const message = this.langService.translate('notifications.actions.delete', { entity: this.entity });
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<DeleteResponse<SenderSubscription>>('Senders.Delete', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap((resp) => {
					if (resp.Success) {
						this.refresh$.next();
					}
				})
			);
	}

	private readonly errorNotifier = (): ErrorNotifierConfig => ({ title: this.title });
}
